import { ConfirmPopup, type ConfirmPopupProps } from '@neo/blocks/confirm-popup'
import { CxDataTable } from '@neo/blocks/cx-data-table/components/cx-data-table'
import type { DataTableRow } from '@neo/blocks/cx-data-table/types'
import { FacetedFilter } from '@neo/blocks/faceted-filter'
import { cn } from '@neo/lib/utils'
import { Typography } from '@neo/primitives/typography'
import { ReactComponent as IcIntegrationBadge } from '@shared/assets/icons/ic-integration-badge.svg'
import { orderBy } from 'lodash'
import { Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import { IntegrationSideSheet } from 'v2/pages/settings/integrations/apps/integration-side-sheet'
import {
  invalidateExternalAppQueries,
  useDeleteOrgAppConnectionMutation,
  useExternalOrgAppsQuery,
  useUpdateOrgAppConnectionMutation,
} from 'v2/pages/settings/integrations/queries'
import { useUsersQuery } from 'v2/pages/settings/queries'
import { ButtonWithPermission } from 'v2/shared/components/button-with-permission'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import {
  type IntegrationErrorEventData,
  type IntegrationSuccessEventData,
  PubnubEvents,
} from 'v2/types/pubnub'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { errorToast, generateToastsForMutation, successToast } from 'v2/utils/toasts'
import { IntegrationState } from '../utils/constants'
import { CustomFieldsDialog } from './custom-fields-dialog'
import type { ExternalAppRow } from './external-app-actions'
import {
  buildGroupedTableRows,
  getColumns,
  getExternalAppsContextMenu,
  integrationStatusOptions,
} from './external-apps.config'
import { useReconnectApp } from './hooks/use-reconnect-app'

const ExternalApps = () => {
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [popupState, setPopupState] = useState<ConfirmPopupProps>({})
  const [customFieldsApp, setCustomFieldsApp] = useState<RExternalOrgApp | null>(null)
  const isAnyPopupOpen = popupState.open || false

  const { data: apps = [], isLoading, isError, refetch } = useExternalOrgAppsQuery()
  const [isRetrying, setIsRetrying] = useState(false)
  const { mutate: updateOrgAppConnection } = useUpdateOrgAppConnectionMutation()
  const { mutate: deleteOrgAppConnection } = useDeleteOrgAppConnectionMutation()
  const { details: currentUser } = useAuth()
  const { hasPermission } = usePermissions()
  const { reconnectApp } = useReconnectApp()

  // The ACI bridge stores only the owner's aom-id on a connection (no CX user directory),
  // so resolve the owner's display name from the org user list here (same source the share
  // dialog uses). This drives the Owner avatar + the account-identifier fallback label.
  const { data: usersData } = useUsersQuery()
  const userMap = useMemo(() => {
    const m: Record<string, string> = {}
    for (const u of usersData?.members_list ?? []) m[u.aom_uuid] = u.account_name
    return m
  }, [usersData])

  // Business logic handlers for context menu actions
  const handleToggleState = useCallback(
    (app: ExternalAppRow) => {
      const state = !(
        app?.state === IntegrationState.ACTIVE || app?.state === IntegrationState.EXPIRED
      )
      const payload = {
        app_uuid: app?.id,
        connection_details: app?.connection_details,
        enabled: state,
        priority_order: app?.priority,
      }
      updateOrgAppConnection(
        { payload, appId: app.id },
        generateToastsForMutation(
          {
            title: 'Success',
            description: `Successfully ${state ? 'enabled' : 'disabled'} the app`,
            callback: () => {
              setPopupState({ open: false })
            },
          },
          { title: 'Error', description: 'Failed to update app' },
        ),
      )
    },
    [updateOrgAppConnection],
  )

  const handleReconnect = useCallback(
    (app: ExternalAppRow) => {
      reconnectApp(app)
    },
    [reconnectApp],
  )

  const handleDelete = useCallback(
    (app: ExternalAppRow) => {
      deleteOrgAppConnection(
        { appId: app.id },
        generateToastsForMutation(
          {
            title: 'Success',
            description: `Successfully deleted the ${app.name} app`,
            callback: () => {
              setPopupState({ open: false })
            },
          },
          { title: 'Error', description: `Failed to delete the ${app.name} app` },
        ),
      )
    },
    [deleteOrgAppConnection],
  )

  const handleCustomFields = useCallback((app: ExternalAppRow) => {
    setCustomFieldsApp(app)
  }, [])

  // Transform and filter data for grouped table
  const tableData = useMemo(() => {
    if (!apps || apps.length === 0) return []

    // Filter individual apps before grouping
    let filteredApps = apps
    if (statusFilter?.length > 0) {
      filteredApps = apps.filter((app) => statusFilter.includes(app.state?.toUpperCase()))
    }

    // Fill the owner's display name from the org user directory so the Owner avatar and the
    // account-identifier fallback render (the backend only knows the aom-id, not the name).
    const orderedApps = orderBy(filteredApps, ['name'], ['asc']).map((app) =>
      app.integrated_by?.id && userMap[app.integrated_by.id]
        ? { ...app, integrated_by: { ...app.integrated_by, name: userMap[app.integrated_by.id] } }
        : app,
    )
    return buildGroupedTableRows(orderedApps)
  }, [apps, statusFilter, userMap])

  useEffect(() => {
    const unbinds = [
      pubnubEventBus.on(PubnubEvents.INTEGRATION_SUCCESS, (data: IntegrationSuccessEventData) => {
        successToast({
          title: 'Success',
          description: data.message,
        })
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_UPDATED, () => {
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_ACCESS_UPDATED, () => {
        invalidateExternalAppQueries()
      }),
      pubnubEventBus.on(PubnubEvents.INTEGRATION_FAILED, (data: IntegrationErrorEventData) => {
        errorToast({
          title: 'Error',
          description: data.error,
        })
      }),
    ]
    return () => {
      unbinds.forEach((unbind) => unbind())
    }
  }, [])

  const handleNewIntegration = () => {
    setIsSideSheetOpen(true)
  }

  // for table cell classes
  const getTableCellClasses = (columnId: string, cellType: 'head' | 'body') => {
    const isHead = cellType === 'head'
    const baseClasses = isHead
      ? 'h-12 text-left align-middle font-jetbrains text-label uppercase text-tertiary [&:has([role=checkbox])]:pr-0'
      : 'py-2 align-middle font-inter text-s-400 [&:has([role=checkbox])]:pr-0 whitespace-nowrap'

    switch (columnId) {
      case 'expander':
        return cn(baseClasses, 'pl-4 pr-0')
      case 'name':
        return cn(baseClasses, 'px-0')
      default:
        return cn(baseClasses, 'px-4')
    }
  }

  // Table configuration
  const tableProps = {
    columns: getColumns(currentUser),
    data: tableData,
    enableSorting: false,
    enableExpanding: true,
    enableHiding: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    showRowsPerPage: true,
    manualPagination: false,
    manualFiltering: false,
    initialState: {
      showGlobalFilter: true,
    },
    state: {
      isLoading,
    },
    // Context menu configuration
    getContextMenu: (
      row?: ExternalAppRow,
      data?: DataTableRow<ExternalAppRow>[],
      hoveredElement?: React.RefObject<HTMLElement | null>,
    ) =>
      getExternalAppsContextMenu(
        row,
        data,
        hoveredElement,
        {
          setPopupState,
          hasPermission,
          handleToggleState,
          handleReconnect,
          handleDelete,
          handleCustomFields,
        },
        currentUser,
      ),
    idKey: 'id',
    cxTableSearchTextInputProps: {
      hideSearchIcon: true,
      placeholder: 'Search by app name...',
      inputClassName: '!w-64 m-0.5 font-inter text-s-400',
    },
    cxTableBodyCellProps: ({ column }: { column: { id: string } }) => ({
      className: getTableCellClasses(column.id, 'body'),
    }),
    cxTableHeadCellProps: ({ column }: { column: { id: string } }) => ({
      className: getTableCellClasses(column.id, 'head'),
    }),
    cxTableBodyRowProps: (params: {
      row: { original: ExternalAppRow; getCanExpand?: () => boolean; toggleExpanded?: () => void }
    }) => ({
      className: 'group hover:bg-hv1 cursor-pointer h-[48px]',
      onClick: (e: React.MouseEvent) => {
        // Don't handle row click if any popup is open or right-clicking for context menu
        if (isAnyPopupOpen || e.button === 2) {
          return
        }

        // Handle group rows (expand/collapse)
        if (params.row.original.isGroup && params.row.getCanExpand?.()) {
          params.row.toggleExpanded?.()
        }
      },
    }),
    renderCustomFilter: () => (
      <FacetedFilter
        title="Status"
        options={integrationStatusOptions}
        value={statusFilter}
        onChange={(values) => setStatusFilter(values as string[])}
        multiSelect
        showResetButton
        className="w-auto min-w-[200px]"
      />
    ),
    renderToolbarInternalActions: () => (
      <ButtonWithPermission
        onClick={() => setIsSideSheetOpen(true)}
        module={RbacModules.integration}
        permissionType={RbacPermissionTypes.create}
      >
        <Plus className="h-4 w-4 mr-2" />
        Connect
      </ButtonWithPermission>
    ),
  }

  const EmptyState = ({ onNewIntegration }: { onNewIntegration: () => void }) => (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg bg-card py-20">
      <div className="flex flex-col items-start max-w-md">
        <IcIntegrationBadge className="mb-4" />
        <Typography variant="H3/500" className="mb-2">
          Integrate with your favorite tools
        </Typography>
        <Typography className="mb-6 text-secondary">
          Connect seamlessly to the platforms
          <br />
          and services that power your business.
        </Typography>
        <ButtonWithPermission
          onClick={onNewIntegration}
          className="flex items-center w-full"
          module={RbacModules.integration}
          permissionType={RbacPermissionTypes.create}
          disabledOnNoPermission
          size="sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Connect
        </ButtonWithPermission>
      </div>
    </div>
  )

  return (
    <>
      {(isError || isRetrying) && (
        <div className="mb-4">
          <QueryError
            title="Couldn't load tool connections."
            testId="button-retry-tool-connections"
            isRetrying={isRetrying}
            onRetry={async () => {
              setIsRetrying(true)
              try {
                await refetch()
              } finally {
                setIsRetrying(false)
              }
            }}
          />
        </div>
      )}
      {(isError || isRetrying) && apps.length === 0 ? null : !isLoading && apps.length === 0 ? (
        <EmptyState onNewIntegration={handleNewIntegration} />
      ) : (
        <CxDataTable<ExternalAppRow> {...tableProps} />
      )}
      {isSideSheetOpen && <IntegrationSideSheet onClose={() => setIsSideSheetOpen(false)} />}
      <ConfirmPopup {...popupState} setOpen={(open) => setPopupState({ ...popupState, open })} />
      {customFieldsApp && (
        <CustomFieldsDialog
          app={customFieldsApp}
          open={!!customFieldsApp}
          onOpenChange={(open) => !open && setCustomFieldsApp(null)}
        />
      )}
    </>
  )
}

export default ExternalApps
